import React, { useEffect, useMemo, useState } from "react";
import api from "../api";

const ACTIONS = ["create", "read", "update", "delete"];

const badge = (allowed) =>
  allowed
    ? "inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200"
    : "inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold bg-slate-100 text-slate-500 border border-slate-200";

const UserRoleMatrix = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [roles, setRoles] = useState([]);
  const [baseMatrix, setBaseMatrix] = useState({});
  const [rows, setRows] = useState([]);

  const [editingUser, setEditingUser] = useState(null);
  const [draftRoles, setDraftRoles] = useState([]);
  const [draftOverride, setDraftOverride] = useState({});

  const load = async (searchValue = "") => {
    setLoading(true);
    setError("");
    try {
      const [matrixRes, usersRes] = await Promise.all([
        api.get("/admin/role-matrix"),
        api.get("/admin/role-matrix/users", { params: { page: 1, limit: 200, search: searchValue || undefined } }),
      ]);
      setRoles(matrixRes?.data?.roles || []);
      setBaseMatrix(matrixRes?.data?.role_matrix || {});
      setRows(usersRes?.data?.data || []);
    } catch (e) {
      setError(e?.response?.data?.message || "Failed to load user role matrix");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load("");
  }, []);

  const moduleKeys = useMemo(() => Object.keys(baseMatrix || {}), [baseMatrix]);

  const openEdit = (row) => {
    setEditingUser(row);
    setDraftRoles(Array.isArray(row?.roles) ? row.roles : []);
    const start = {};
    moduleKeys.forEach((moduleName) => {
      start[moduleName] = {};
      ACTIONS.forEach((action) => {
        start[moduleName][action] = !!row?.effective_matrix?.[moduleName]?.[action];
      });
    });
    setDraftOverride(start);
  };

  const toggleRole = (roleName) => {
    setDraftRoles((prev) => {
      const exists = prev.includes(roleName);
      if (exists) return prev.filter((r) => r !== roleName);
      return [...prev, roleName];
    });
  };

  const togglePermission = (moduleName, action) => {
    setDraftOverride((prev) => ({
      ...prev,
      [moduleName]: {
        ...(prev[moduleName] || {}),
        [action]: !prev?.[moduleName]?.[action],
      },
    }));
  };

  const handleSave = async () => {
    if (!editingUser) return;
    if (!draftRoles.length) {
      setError("Select at least one role for the user");
      return;
    }
    setSaving(true);
    setError("");
    try {
      await api.put(`/admin/role-matrix/users/${editingUser.id}`, {
        roles: draftRoles,
        matrix_override: draftOverride,
      });
      setEditingUser(null);
      await load(query);
    } catch (e) {
      setError(e?.response?.data?.message || "Failed to save user role matrix");
    } finally {
      setSaving(false);
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    await load(query.trim());
  };

  return (
    <div className="w-full">
      <div className="bg-white border border-slate-200 rounded-xl p-4 md:p-5 shadow-sm">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div>
            <h2 className="text-lg font-bold text-slate-900">User Wise Role Matrix</h2>
            <p className="text-sm text-slate-500 mt-1">
              Edit CRUD permissions and assign multiple roles for each individual user.
            </p>
          </div>
          <form onSubmit={handleSearch} className="flex items-center gap-2">
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search user"
              className="px-3 py-2 rounded-lg border border-slate-300 text-sm text-slate-800 bg-white"
            />
            <button type="submit" className="px-3 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700">
              Search
            </button>
          </form>
        </div>
      </div>

      <div className="mt-4 bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-6 text-sm text-slate-500">Loading role matrix...</div>
        ) : error ? (
          <div className="p-6 text-sm text-red-700 bg-red-50 border-t border-red-100">{error}</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-[980px] w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left px-4 py-3 font-semibold text-slate-600">User</th>
                  <th className="text-left px-4 py-3 font-semibold text-slate-600">Roles</th>
                  {moduleKeys.map((m) => (
                    <th key={m} className="text-left px-4 py-3 font-semibold text-slate-600 capitalize">
                      {m}
                    </th>
                  ))}
                  <th className="text-left px-4 py-3 font-semibold text-slate-600">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((u) => (
                  <tr key={u.id} className="border-b border-slate-100">
                    <td className="px-4 py-3">
                      <div className="font-medium text-slate-900">{u.name}</div>
                      <div className="text-xs text-slate-500">{u.email}</div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        {(u.roles || []).map((roleName) => (
                          <span key={roleName} className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold bg-blue-50 text-blue-700 border border-blue-200">
                            {roleName}
                          </span>
                        ))}
                      </div>
                    </td>
                    {moduleKeys.map((m) => (
                      <td key={`${u.id}-${m}`} className="px-4 py-3">
                        <div className="flex flex-wrap gap-1">
                          {ACTIONS.map((a) => (
                            <span key={a} className={badge(!!u?.effective_matrix?.[m]?.[a])}>
                              {a[0].toUpperCase()}
                            </span>
                          ))}
                        </div>
                      </td>
                    ))}
                    <td className="px-4 py-3">
                      <button
                        type="button"
                        onClick={() => openEdit(u)}
                        className="px-3 py-2 rounded-lg border border-slate-300 text-slate-700 text-xs font-semibold hover:bg-slate-50"
                      >
                        Edit
                      </button>
                    </td>
                  </tr>
                ))}
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={3 + moduleKeys.length} className="px-4 py-6 text-center text-slate-500">
                      No users found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {editingUser && (
        <div className="fixed inset-0 z-50 bg-black/40 p-3 md:p-6 flex items-center justify-center">
          <div className="w-full max-w-4xl bg-white rounded-xl border border-slate-200 shadow-lg overflow-hidden">
            <div className="p-4 md:p-5 border-b border-slate-200 flex items-center justify-between gap-3">
              <div>
                <h3 className="text-base md:text-lg font-bold text-slate-900">Edit User Matrix</h3>
                <p className="text-xs md:text-sm text-slate-500">{editingUser.name} ({editingUser.email})</p>
              </div>
              <button
                type="button"
                onClick={() => setEditingUser(null)}
                className="px-3 py-2 rounded-lg border border-slate-300 text-slate-700 text-sm"
              >
                Close
              </button>
            </div>

            <div className="p-4 md:p-5 space-y-4 max-h-[75vh] overflow-y-auto">
              <div>
                <div className="text-sm font-semibold text-slate-800 mb-2">Assign Roles</div>
                <div className="flex flex-wrap gap-2">
                  {roles.map((roleName) => {
                    const checked = draftRoles.includes(roleName);
                    return (
                      <button
                        key={roleName}
                        type="button"
                        onClick={() => toggleRole(roleName)}
                        className={
                          checked
                            ? "px-3 py-1.5 rounded-lg border text-xs font-semibold bg-blue-600 text-white border-blue-700"
                            : "px-3 py-1.5 rounded-lg border text-xs font-semibold bg-white text-slate-700 border-slate-300"
                        }
                      >
                        {roleName}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="min-w-[760px] w-full text-sm">
                  <thead className="bg-slate-50 border border-slate-200">
                    <tr>
                      <th className="text-left px-4 py-3 font-semibold text-slate-600">Module</th>
                      <th className="text-center px-4 py-3 font-semibold text-slate-600">Create</th>
                      <th className="text-center px-4 py-3 font-semibold text-slate-600">Read</th>
                      <th className="text-center px-4 py-3 font-semibold text-slate-600">Update</th>
                      <th className="text-center px-4 py-3 font-semibold text-slate-600">Delete</th>
                    </tr>
                  </thead>
                  <tbody>
                    {moduleKeys.map((moduleName) => (
                      <tr key={moduleName} className="border-b border-x border-slate-200">
                        <td className="px-4 py-3 font-medium text-slate-900 capitalize">{moduleName}</td>
                        {ACTIONS.map((action) => (
                          <td key={`${moduleName}-${action}`} className="px-4 py-3 text-center">
                            <input
                              type="checkbox"
                              checked={!!draftOverride?.[moduleName]?.[action]}
                              onChange={() => togglePermission(moduleName, action)}
                              className="h-4 w-4 accent-blue-600"
                            />
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="p-4 md:p-5 border-t border-slate-200 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => setEditingUser(null)}
                className="px-3 py-2 rounded-lg border border-slate-300 text-slate-700 text-sm"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                className="px-3 py-2 rounded-lg bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 disabled:opacity-60"
              >
                {saving ? "Saving..." : "Save User Matrix"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserRoleMatrix;
